interface GraphState {
  positions: Record<string, { x: number; y: number }>
  zoomScale?: number
  /** See LAYOUT_VERSION. Absent on anything written before the stamp existed. */
  layoutVersion?: number
}

/**
 * What the STORED POSITIONS WERE PRODUCED BY. A restored position wins over the ring plan —
 * `loadPosition` fills `x`/`y` only when they are undefined and `Layout.seedPositions` returns
 * early for a node that already has them — and the force model does not wash the seed out
 * within a run. So a reader who has been here before is shown the layout of their previous
 * visit, refined, rather than the layout this build draws.
 *
 * ⚠⚠ THAT MADE THE DRAWN SIZE DEPEND ON THE READER'S HISTORY (app#314). Measured on one build,
 * one fixture and one 375x812 window: `0.5564` fitted scale with the store cleared and `0.5077`
 * with it carrying earlier runs — 8.8%, and both stable to every digit across reloads, so a
 * second fixed point rather than simulation noise. Two readers with the same fleet were shown
 * the graph at different sizes and neither could tell why.
 *
 * ⚠⚠ BUMP THIS IN THE SAME COMMIT AS ANY CHANGE TO THE RING TABLE, THE ANGLE PLAN OR THE FORCE
 * MODEL. That is the whole mechanism: a returning reader takes the new layout ONCE, from the
 * rings, and keeps their stable picture on every visit after it. Leaving it alone through a
 * layout change is what ships that change to new readers only — which is the second half of
 * app#314, and it is silent.
 *
 * ⚠ Persistence itself is deliberate and stays. Dropping it costs the stable picture between
 * visits and does not pay for itself; the defect was never that positions are kept, it was that
 * they outlived the layout that produced them.
 */
// 2: one hidden root holds every network (`root.ts`, 2026-10-21). Its id is new, and an org with
// networks of two owners had two roots, each with its own wedge.
const LAYOUT_VERSION = 2

class State {
  private storageKey = 'graphState'
  private positions: Record<string, { x: number; y: number }> = {}
  private zoomScale?: number
  private centered = false

  constructor() {
    this.load()
  }

  /**
   * ⚠⚠ GUARDED ON THE ACCESS, NOT ON THE VALUE (app#311). A browser told to block site data
   * throws on `localStorage` ITSELF, and this runs while the module is evaluated — the file ends
   * `export default new State()` and the constructor calls straight into here, so the throw
   * happens before a render and before an ErrorBoundary could exist (the app has none).
   * `components/Graph.tsx` imports `graph/core`, which imports this, so the WHOLE PAGE failed to
   * start: the reader saw a blank app, not a lost preference. The old try/catch sat INSIDE the
   * `if` and only ever covered `JSON.parse`.
   *
   * ⚠ `lib/viewPrefs.ts` guards the same storage for the same reason and named this file as the
   * remaining unguarded reader.
   */
  load() {
    try {
      const state = localStorage.getItem(this.storageKey)
      if (!state) return
      const parsed: GraphState = JSON.parse(state)
      // ⚠ A store written by a DIFFERENT layout is discarded rather than migrated: there is
      // nothing to migrate, because the coordinates themselves are what the old layout produced.
      // Absent is a mismatch too — anything written before the stamp existed predates it.
      if (parsed.layoutVersion !== LAYOUT_VERSION) return
      this.positions = parsed.positions || {}
      this.zoomScale = parsed.zoomScale
    } catch (e) {
      // A browser that blocks site data, a quota error, or an unparseable value. All three mean
      // the same thing here — start from the rings — and none of them is worth a blank page.
      console.warn('graph state could not be read; starting from the layout plan', e)
    }
  }

  save() {
    const state: GraphState = {
      positions: this.positions,
      zoomScale: this.zoomScale,
      layoutVersion: LAYOUT_VERSION,
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state))
    } catch (e) {
      // ⚠ Writing throws in exactly the same browsers reading does, and on quota. Losing the
      // saved layout costs a reader their stable picture on the next visit; throwing here costs
      // them the page they are on, because this is called from the simulation's end handler.
      console.warn('graph state could not be saved', e)
    }
  }

  reset() {
    this.positions = {}
    this.zoomScale = undefined
    this.centered = false
    this.save()
  }

  setZoomScale(scale: number) {
    this.zoomScale = scale
  }

  getZoomScale(): number | undefined {
    return this.zoomScale
  }

  setPosition(node: GraphNode) {
    if (node.x && node.y) this.positions[node.id] = { x: node.x, y: node.y }
  }

  setPositions(nodes: GraphNode[]) {
    this.positions = {}
    nodes.forEach(node => this.setPosition(node))
  }

  removePosition(node: GraphNode) {
    delete this.positions[node.id]
  }

  removePositions(nodes: GraphNode[]) {
    for (const node of nodes) {
      this.removePosition(node)
    }
  }

  loadPosition(node: GraphNode): boolean {
    if (this.positions[node.id] && (node.x === undefined || node.y === undefined)) {
      node.x = this.positions[node.id].x
      node.y = this.positions[node.id].y
      return true
    }
    return false
  }

  /**
   * Load saved positions for nodes that don't have coordinates yet.
   * @returns true if any nodes were loaded (i.e., need layout)
   */
  loadPositions(nodes: GraphNode[]): boolean {
    let missing = false
    const loadedNodes: GraphNode[] = []
    for (const node of nodes) {
      if (!this.loadPosition(node)) {
        missing = true
      } else {
        loadedNodes.push(node)
      }
    }

    if (!this.centered && loadedNodes.length) {
      this.centerLoadedNodes(loadedNodes)
      this.centered = true
    }
    return missing
  }

  private centerLoadedNodes(loadedNodes: GraphNode[]) {
    const xs = loadedNodes.map(n => n.x ?? 0)
    const ys = loadedNodes.map(n => n.y ?? 0)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2

    const offsetX = -centerX
    const offsetY = -centerY

    loadedNodes.forEach(node => {
      if (node.x !== undefined && node.y !== undefined) {
        node.x += offsetX
        node.y += offsetY
        const pos = this.positions[node.id]
        if (pos) {
          pos.x += offsetX
          pos.y += offsetY
        }
      }
    })
  }
}

export default new State()
